import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { can } from '@kronos/domain'
import type { RemovalReason, Role } from '@kronos/domain'
import { repository } from '../data'
import { Breadcrumb, History, LocationBadge, Positions } from './component/ComponentParts'
import { InstallChain } from './InstallChain'
import { HeroImage, PhotoStrip } from './Photos'
import { Badge, Panel, Section } from './primitives'
import { navigate } from './router'

const REASONS: readonly { readonly id: RemovalReason; readonly label: string }[] = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'failure', label: 'It failed' },
  { id: 'upgrade', label: 'Upgrade' },
  { id: 'cannibalized', label: 'Needed elsewhere' },
]

const KIND: Readonly<Record<string, string>> = { build: 'Build', maintenance: 'Inspection', repair: 'Repair', teardown: 'Teardown' }

/**
 * One page for every part, assembly and robot — where a scan lands (§9).
 *
 * **Only actions that work, and that you may take.** The page used to show
 * every action the lifecycle allowed, with the unbuilt ones greyed out and no
 * reason given, and ignored the person's role so the server refused them
 * afterwards. Now an action appears if it is wired up and `can()` says yes.
 */
export function ComponentView({ id, roles }: { id: string; roles: readonly Role[] }): JSX.Element {
  const queryClient = useQueryClient()
  const [removing, setRemoving] = useState(false)
  const [installing, setInstalling] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const q = useQuery({ queryKey: ['component', id], queryFn: () => repository.component(id) })
  const procedures = useQuery({ queryKey: ['procedures', id], queryFn: () => repository.proceduresFor(id) })
  const startWork = useMutation({
    mutationFn: (procedureId: string) => repository.openExecution(procedureId, id),
    onSuccess: (execution) => navigate({ name: 'execution', id: execution.id }),
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })
  const remove = useMutation({
    mutationFn: (reason: RemovalReason) => repository.remove(q.data?.serial ?? '', reason),
    onSuccess: () => {
      setRemoving(false)
      setProblem(null)
      void queryClient.invalidateQueries()
    },
    onError: (e: Error) => setProblem(e.message.replace(/^\d+:\s*/, '')),
  })

  if (q.isPending) return <p className="muted">Loading…</p>
  if (q.isError || !q.data) return <p className="critical">Could not load that part.</p>
  const c = q.data
  const mayInstall = c.actions.includes('install') && can(roles, 'install_component')
  const mayRemove = c.actions.includes('remove') && can(roles, 'remove_component')
  const work = c.location === 'scrapped' || !can(roles, 'open_execution') ? [] : (procedures.data ?? [])

  return (
    <div className="page">
      <Breadcrumb component={c} />
      <header className="object-header">
        {c.image ? <HeroImage image={c.image} alt={`${c.typeName} ${c.serial}`} /> : null}
        <div className="object-header__text">
          <span className="page-header__eyebrow">
            {c.category}
            {c.supplier ? ` · ${c.supplier}` : ''}
          </span>
          <h1 className="page-header__title">
            {c.typeName}
            {c.revisionName ? <span className="muted"> {c.revisionName}</span> : null}
          </h1>
          <span className="serial object-header__serial">{c.serial}</span>
          {c.supplierSerial ? <span className="muted">Maker’s serial {c.supplierSerial}</span> : null}
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <LocationBadge location={c.location} />
            {c.path ? <Badge>{c.path.replace(/_/g, ' ')}</Badge> : null}
            {c.openFailureReports > 0 ? (
              <Badge tone="critical">
                {c.openFailureReports} open failure{c.openFailureReports > 1 ? 's' : ''}
              </Badge>
            ) : null}
          </div>
          {mayInstall || mayRemove ? (
            <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {mayInstall ? <button className="primary" onClick={() => setInstalling(true)}>Fit it into…</button> : null}
              {mayRemove ? <button onClick={() => setRemoving(true)}>Take it out</button> : null}
            </div>
          ) : null}
        </div>
      </header>

      {problem ? <p className="critical" role="alert">{problem}</p> : null}

      {installing ? (
        <InstallChain
          componentId={c.id}
          componentSerial={c.serial}
          canOverride={can(roles, 'override_deviation')}
          onDone={() => {
            setInstalling(false)
            void queryClient.invalidateQueries()
          }}
          onCancel={() => setInstalling(false)}
        />
      ) : null}

      {removing ? (
        <Panel padded>
          <div className="stack" style={{ gap: 10 }}>
            <strong>Why is it coming out?</strong>
            <span className="muted">It goes to the depot, with anything fitted inside it.</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {REASONS.map((r) => (
                <button key={r.id} onClick={() => remove.mutate(r.id)} disabled={remove.isPending}>
                  {r.label}
                </button>
              ))}
              <button className="ghost" onClick={() => setRemoving(false)}>Cancel</button>
            </div>
          </div>
        </Panel>
      ) : null}

      {work.length > 0 ? (
        <Section label="Start a job">
          <Panel>
            <div className="row-list">
              {work.map((p) => (
                <button key={p.id} className="row-item" disabled={startWork.isPending} onClick={() => startWork.mutate(p.id)}>
                  <span className="row-item__main">
                    <span className="row-item__title">{p.name}</span>
                    <span className="row-item__meta">
                      {KIND[p.kind] ?? p.kind} · {p.steps} steps{p.estimatedMinutes ? ` · about ${p.estimatedMinutes} min` : ''}
                    </span>
                  </span>
                  <span className="row-item__side">Start →</span>
                </button>
              ))}
            </div>
          </Panel>
        </Section>
      ) : null}

      <Positions component={c} />
      <PhotoStrip subject={{ kind: 'component', id: c.id }} canEdit={c.location !== 'scrapped'} canDelete={can(roles, 'delete_attachment')} />
      <History component={c} />
    </div>
  )
}
